import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { apiClient } from '@services/api-client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VacancyYear { year: number; rate: number | null; count?: number | null }
interface VacancyScope { years: VacancyYear[]; name?: string | null; code?: string | null }

interface VacancyRatesData {
  commune: VacancyScope & { bfsNr: number };
  canton: VacancyScope & { ofsCode?: number | null };
  switzerland: VacancyScope;
  rooms: string;
  latestYear: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROOMS_OPTIONS = [
  { value: '_T', label: 'Tous' },
  { value: '1',  label: '1p' },
  { value: '2',  label: '2p' },
  { value: '3',  label: '3p' },
  { value: '4',  label: '4p' },
  { value: '5',  label: '5p' },
  { value: '6+', label: '6p+' },
];

const fmtCount = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1).replace('.', '\'')} k` : String(n);

const trendColor = (current: number, prev: number) => {
  const diff = current - prev;
  if (Math.abs(diff) < 1) return 'text-gray-500 dark:text-gray-400';
  return diff > 0
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-emerald-600 dark:text-emerald-400';
};

const trendArrow = (current: number, prev: number) => {
  const diff = current - prev;
  if (Math.abs(diff) < 1) return '→';
  return diff > 0 ? '↑' : '↓';
};

// ─── Sparkline ────────────────────────────────────────────────────────────────

const Sparkline: React.FC<{ data: VacancyYear[]; color: string }> = ({ data, color }) => {
  if (data.length < 2) return null;
  const points = data.map((d) => ({ year: d.year, count: d.count ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={48}>
      <LineChart data={points} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <XAxis dataKey="year" hide />
        <YAxis hide domain={['auto', 'auto']} />
        <Tooltip
          formatter={(v) => [fmtCount(Number(v)), '']}
          labelFormatter={(l) => `${l}`}
          contentStyle={{ fontSize: 11 }}
        />
        <Line
          type="monotone"
          dataKey="count"
          stroke={color}
          strokeWidth={2}
          dot={{ r: 2, fill: color }}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  bfsNr: number;
  defaultRooms?: string;
}

export const VacancyRateCard: React.FC<Props> = ({ bfsNr, defaultRooms = '_T' }) => {
  const [rooms, setRooms] = useState(defaultRooms);
  const [data, setData] = useState<VacancyRatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNoData(false);
    apiClient
      .get<VacancyRatesData>(`/communes/vacancy/${bfsNr}`, { params: { rooms } })
      .then((d) => {
        const hasAny =
          d.commune.years.length > 0 ||
          d.canton.years.length > 0 ||
          d.switzerland.years.length > 0;
        setData(d);
        setNoData(!hasAny);
      })
      .catch(() => setNoData(true))
      .finally(() => setLoading(false));
  }, [bfsNr, rooms]);

  return (
    <div className="rounded-xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/10 p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
          Logements vacants à louer (OFS LWZ)
        </h3>
        {/* Rooms filter */}
        <div className="flex gap-1 flex-wrap justify-end">
          {ROOMS_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setRooms(o.value)}
              className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                rooms === o.value
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-amber-300'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Chargement...
        </div>
      )}

      {!loading && noData && (
        <p className="text-sm text-gray-400 dark:text-gray-500 py-2">
          Données non disponibles — lancez un refresh OFS depuis l'administration.
        </p>
      )}

      {!loading && data && !noData && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Commune */}
          <ScopeBlock
            title={data.commune.name ?? 'Commune'}
            subtitle="Commune"
            years={data.commune.years}
            color="#f59e0b"
          />
          {/* Canton */}
          <ScopeBlock
            title={data.canton.code ? `Canton ${data.canton.code}` : 'Canton'}
            subtitle="Canton"
            years={data.canton.years}
            color="#8b5cf6"
          />
          {/* Switzerland */}
          <ScopeBlock
            title="Suisse"
            subtitle="Moyenne nationale"
            years={data.switzerland.years}
            color="#3b82f6"
          />
        </div>
      )}

      {!loading && data?.latestYear && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          Source : OFS LWZ · Logements vacants à louer · Recensement annuel au 1er juin · {data.latestYear}
        </p>
      )}
    </div>
  );
};

// ─── ScopeBlock ───────────────────────────────────────────────────────────────

const ScopeBlock: React.FC<{
  title: string;
  subtitle: string;
  years: VacancyYear[];
  color: string;
}> = ({ title, subtitle, years, color }) => {
  if (years.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 opacity-60">
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">{subtitle}</p>
        <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">{title}</p>
        <p className="text-sm text-gray-400">N/D</p>
      </div>
    );
  }

  const latest = years[years.length - 1];
  const prev = years.length >= 2 ? years[years.length - 2] : null;
  const latestCount = latest.count ?? 0;
  const prevCount = prev?.count ?? null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{subtitle}</p>
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 truncate">{title}</p>
      <p className="text-xl font-bold text-gray-800 dark:text-gray-100">
        {fmtCount(latestCount)}
        <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-1">logts</span>
      </p>
      {prevCount !== null && (
        <span className={`text-xs font-medium ${trendColor(latestCount, prevCount)}`}>
          {trendArrow(latestCount, prevCount)}{' '}
          {Math.abs(latestCount - prevCount)} vs {prev!.year}
        </span>
      )}
      <div className="mt-2">
        <Sparkline data={years} color={color} />
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
        {years[0].year}–{latest.year}
      </p>
    </div>
  );
};
